import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { forkJoin, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslateModule } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { MediaUploadModule } from '@shared/components/media-upload/media-upload.module';
import { MediasListComponent } from '@shared/components/recipes-form-header/classifications/medias-list/medias-list.component';
import { ComponentsModule } from '@shared/components/components.module';
import { PipesModule } from '@shared/pipes/pipes.module';
import { CatalogComponent } from '@shared/components/recipes-form-header/classifications/catalog/catalog.component';
import { MatChipsModule } from '@angular/material/chips';
import { RecipeLite } from '@shared/models/recipe/recipe-lite.model';
import { ResourceMediaLite } from '@shared/models/generic/media/resource-media-lite.model';
import { DataRef, DigitalAsset } from '@shared/models/dataref.model';
import { RecipeService } from '@services/recipe/recipe.service';
import { NotificationService } from '@services/notification.service';
import { RefDataService } from '@services/ref-data.service';
import { ConfigService } from '@services/config.service';
import { LocalizedValue } from '@shared/models/localized-value.model';
import { PackLite } from '@shared/models/generic/pack-lite.model';
import { Utils } from '@shared/utils/utils';
import { SharedModule } from '@shared/shared.module';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';

@Component({
  selector: 'app-classifications',
  templateUrl: 'classifications.component.html',
  styleUrls: ['./classifications.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  imports: [
    ReactiveFormsModule,
    TranslateModule,
    CommonModule,
    MediaUploadModule,
    MediasListComponent,
    ComponentsModule,
    PipesModule,
    CatalogComponent,
    MatChipsModule,
    SharedModule,
    MatSlideToggleModule,
  ],
})
export class ClassificationsComponent implements OnInit, OnDestroy, OnChanges {
  @Input() abacusType = false;
  @Input() recipeOriginal?: RecipeLite;
  @Input() isReworkAllowed: boolean;
  /**
   * Emits the current form group state each time its value changes
   * True means the formGroup is dirty, false means it's clean
   */
  @Output() classificationsDirty = new EventEmitter<boolean>();
  @Output() updateRecipe = new EventEmitter<Partial<RecipeLite>>();
  @Output() mediaUpdateEvent = new EventEmitter<ResourceMediaLite[]>();
  @Output() formDirtyState = new EventEmitter<boolean>();
  @Output() isAIGeneratedChange = new EventEmitter<boolean>();
  formClassif: UntypedFormGroup = this.formBuilder.group({});
  math = Math;
  digitalAssetsByPack: string[] = [];

  initiatedUpdate = false;

  destroy$ = new Subject<void>();

  isSubmitted = false;
  lang = 'en';
  packs: any = [];
  occasions: any = [];
  courseTypes: any = [];
  excludedFoods: any = [];
  marketingPrograms: any = [];
  refDataPrivacies: any = [];
  recipeClassificationTypes: any = [];
  recipeClassifications: any = [];
  classificationRichInList: any = [];
  digitalAssets: DigitalAsset[] = [];
  partnersList: DataRef[] = [];
  showTree = false;
  isSemanticAllowed = false;
  isSemanticDone = false;
  isOpenPremiumPacks = false;

  difficultyHoverIndex = -1;
  costHoverIndex = -1;
  showDigitalAssets = false;
  digitalAssetDisabled = false;

  constructor(
    private recipeService: RecipeService,
    private notificationService: NotificationService,
    public formBuilder: UntypedFormBuilder,
    private refDataService: RefDataService,
    private configService: ConfigService,
  ) {}

  ngOnInit(): void {
    this.lang = this.configService.currentLanguage;
    this.refDataService.getByKey('excludedFoods', true).subscribe((allExcludedFoods) => {
      this.excludedFoods = this.refDataService.translateReference(this.refDataService.toArray(allExcludedFoods));
    });
    this.refDataService.getByKey('marketingPrograms', true).subscribe((allMarketingPrograms) => {
      this.marketingPrograms = this.refDataService.translateReference(this.refDataService.toArray(allMarketingPrograms));
    });
    this.refDataService.getByKey('privacies', true).subscribe((allPrivacies) => {
      this.refDataPrivacies = this.refDataService.translateReference(this.refDataService.toArray(allPrivacies));
    });

    if (this.recipeOriginal) {
      this.refDataService
        .getByKey('partners', true, this.recipeOriginal.domain, this.recipeOriginal.market, this.recipeOriginal.lang)
        .subscribe((allPartners) => {
          this.partnersList = this.refDataService.toArray(allPartners);
        });
      this.initFormGroup(this.recipeOriginal);
      this.showTree = false;
      this.initDataref(this.recipeOriginal);

      this.refDataService
        .getFeatureFlags('SEMANTIC_ACTIVATION')
        .pipe(takeUntil(this.destroy$))
        .subscribe((semanticAllowed) => {
          this.isSemanticAllowed = semanticAllowed.activatedAreas.some(
            (area) =>
              area.domain.key === this.recipeOriginal?.domain &&
              area.market.key === this.recipeOriginal?.market &&
              area.lang.key === this.recipeOriginal?.lang,
          );
        });

      this.isSemanticDone =
        this.recipeOriginal.detectedNutrition !== null &&
        this.recipeOriginal.detectedNutrition !== undefined &&
        !this.recipeOriginal.detectedNutrition.empty;

      const { domain, digitalAssets } = this.recipeOriginal;
      const isPayableDomainApp = this.configService.getAppConfig().payableDomainApps.includes(domain);
      this.isOpenPremiumPacks = this.configService.getAppConfig().openPremiumPacks.includes(domain);

      this.showDigitalAssets = isPayableDomainApp || Boolean(digitalAssets);
      this.digitalAssetDisabled = Boolean(digitalAssets);

      this.recipeOriginal.packs?.map((pack) =>
        this.controlDigitalAsset(pack, this.recipeOriginal?.market as string, this.recipeOriginal?.lang as string),
      );
    }

    this.configService.currentLanguage$.pipe(takeUntil(this.destroy$)).subscribe((currentLanguage) => {
      if (currentLanguage) {
        this.lang = currentLanguage;
        this.initDataref(this.recipeOriginal!);
      }
    });
    this.formClassif.valueChanges.subscribe(() => {
      this.formDirtyState.emit(this.formClassif.dirty);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.recipeOriginal?.currentValue && this.initiatedUpdate) {
      this.formClassif.markAsPristine();
      this.classificationsDirty.emit(false);
      this.initiatedUpdate = false;
    }
  }

  controlDigitalAsset(pack: string, market: string, lang: string): void {
    if (this.recipeOriginal?.isPremium) {
      this.recipeService.getDigitalAssetsByPack(pack, market, lang).subscribe(
        (digitalAsset: any) => {
          if (digitalAsset) {
            this.initDigitalAsset(digitalAsset);
          }
        },
        (err) => this.errorManager(err),
      );
    }
  }

  errorManager(err: HttpErrorResponse): void {
    this.notificationService.sendAPIError(err, 'ERRORS_RECIPE_ASSOCIATE_PACK_DIGITAL_ASSET');
  }

  initDigitalAsset(digitalAsset: DigitalAsset): void {
    if (digitalAsset.name) {
      (digitalAsset.name as unknown as Array<LocalizedValue>)
        .filter((n: LocalizedValue) => n.lang === this.recipeOriginal?.lang && n.market === this.recipeOriginal?.market)
        .forEach((data: LocalizedValue) => this.digitalAssetsByPack.push(data.value));
    } else {
      this.digitalAssetsByPack = [];
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  digitalAssetHasResource(digitalAsset: DigitalAsset): boolean {
    return digitalAsset.localizedResources?.filter((lr) => lr.resources?.length > 0).length > 0;
  }

  packHasDigitalAsset(pack: PackLite): boolean {
    return (
      this.digitalAssets
        .filter((digitalAsset) => digitalAsset.localizedResources && digitalAsset.localizedResources.length > 0)
        .map((digitalAsset) => digitalAsset.localizedResources)
        .reduce((pre, cur) => pre.concat(cur))
        .filter((localizedResource) => localizedResource.resources && localizedResource.resources.length > 0)
        .map((localizedResource) => localizedResource.resources)
        .reduce((pre, cur) => pre.concat(cur))
        .filter((resource) => resource.resourceId === pack.key).length > 0
    );
  }

  filterWithKey(array: any[], key: string): any[] {
    return array.filter((obj) => obj.type?.key === key);
  }

  closeMediaUpload(): void {
    this.mediaUpdateEvent.emit(this.formClassif.controls.resourceMedias.value);
  }

  setDifficulty(difficulty: number): void {
    this.formClassif.markAsDirty();
    this.formClassif.patchValue({ difficulty });
  }

  setCost(cost: number): void {
    this.formClassif.markAsDirty();
    this.formClassif.patchValue({ cost });
  }

  reset(): void {
    if (this.recipeOriginal) {
      this.formClassif.markAsPristine();
      this.formClassif.setValue({
        difficulty: this.recipeOriginal.difficulty ?? 0,
        cost: this.recipeOriginal.cost ?? 0,
        packs: Utils.cloneArray(this.recipeOriginal.packs ?? []),
        occasions: Utils.cloneArray(this.recipeOriginal.occasions),
        excludedFoods: Utils.cloneArray(this.recipeOriginal.excludedFoods),
        detectedExcludedFoods: Utils.cloneArray(this.recipeOriginal.detectedExcludedFoods),
        courses: Utils.cloneArray(this.recipeOriginal.courses),
        productReferences: Utils.cloneArray(this.recipeOriginal.productReferences),
        classifications: Utils.cloneArray(this.recipeOriginal.classifications),
        classificationRichIn: this.recipeOriginal.richIn ?? '',
        digitalAssets: this.recipeOriginal.digitalAssets ?? [],
        privacyLevel: this.recipeOriginal.privacyLevel ?? 'PUBLIC',
        partner: this.recipeOriginal.partner ?? '',
        resourceMedias: this.recipeOriginal.resourceMedias ?? [],
        chef: this.recipeOriginal.chef ?? '',
        isAIGenerated: this.recipeOriginal.isAIGenerated ?? false,
      });
      this.formDirtyState.emit(this.formClassif.dirty);
    }
  }

  save(): void {
    if (!this.recipeOriginal || this.formClassif.invalid) {
      return;
    }
    this.initiatedUpdate = true;
    this.updateRecipe.emit(this.recipeService.convertFormClassifToRecipeLite(this.recipeOriginal, this.formClassif.value));
    this.formClassif.markAsPristine();
    this.formDirtyState.emit(this.formClassif.dirty);
  }

  private initFormGroup(recipe: RecipeLite): void {
    this.formClassif = this.formBuilder.group({
      difficulty: recipe.difficulty ?? 0,
      cost: recipe.cost ?? 0,
      packs: [Utils.cloneArray(recipe.packs)],
      occasions: [Utils.cloneArray(recipe.occasions)],
      excludedFoods: [Utils.cloneArray(recipe.excludedFoods)],
      detectedExcludedFoods: [Utils.cloneArray(recipe.detectedExcludedFoods)],
      productReferences: [Utils.cloneArray(recipe.productReferences)],
      courses: [Utils.cloneArray(recipe.courses)],
      classifications: [Utils.cloneArray(recipe.classifications)],
      classificationRichIn: [recipe.richIn ?? ''],
      digitalAssets: [recipe.digitalAssets ?? []],
      privacyLevel: [recipe.privacyLevel ?? 'PUBLIC'],
      partner: [recipe.partner ?? ''],
      resourceMedias: [recipe.resourceMedias ?? []],
      chef: [recipe.chef ?? ''],
      isAIGenerated: [recipe.isAIGenerated ?? false],
    });
    this.formClassif.statusChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.classificationsDirty.emit(this.formClassif.dirty));
  }

  private initDataref(recipe: RecipeLite): void {
    if (recipe) {
      const queries = [
        this.refDataService.getByKey('digitalAssets', true, recipe.domain, recipe.market, recipe.lang),
        this.refDataService.getByKey('packs', true, recipe.domain, recipe.market, recipe.lang),
      ];

      forkJoin(queries)
        .pipe(takeUntil(this.destroy$))
        .subscribe((responses) => {
          this.digitalAssets = this.refDataService.translateReference(this.refDataService.toArray(responses[0])) ?? [];
          this.digitalAssets = this.digitalAssets.filter((d) => this.digitalAssetHasResource(d));
          this.packs = this.refDataService.translateReference(this.refDataService.toArray(responses[1]) ?? [], recipe.market, recipe.lang);
          this.packs = this.refDataService.alphabeticOrder(this.packs, 'translatedName') ?? [];
          if (this.recipeOriginal?.isPremium && !this.isOpenPremiumPacks) {
            this.packs = this.packs.filter((p: PackLite) => p.isPremium && this.packHasDigitalAsset(p));
          } else if (!this.recipeOriginal?.isPremium) {
            this.packs = this.packs.filter((p: PackLite) => !p.isPremium);
          }
        });
      this.initDatarefNext(recipe);
    }
  }

  private initDatarefNext(recipe: RecipeLite): void {
    this.refDataService.getByKey('occasions', true, recipe.domain, recipe.market, recipe.lang).subscribe((allOccasions) => {
      this.occasions = this.refDataService.toArray(allOccasions);
      this.occasions = this.occasions.map((occasion: DataRef) => ({
        key: occasion.key,
        translatedName: occasion.label + (occasion.en ? ' (' + occasion.en + ')' : ''),
      }));
      this.occasions = this.refDataService.alphabeticOrder(this.occasions, 'translatedName');
    });

    this.refDataService.getByKey('courseTypes', true, recipe.domain, recipe.market, recipe.lang).subscribe((allCourseTypes) => {
      this.courseTypes = this.refDataService.toArray(allCourseTypes);
      this.courseTypes = this.courseTypes.map((course: DataRef) => ({
        key: course.key,
        translatedName: course.label + (course.en ? ' (' + course.en + ')' : ''),
      }));
      this.courseTypes = this.refDataService.alphabeticOrder(this.courseTypes, 'translatedName');
    });

    this.refDataService
      .getByKey('recipeClassificationTypes', true, recipe.domain, 'GS_FR', 'fr')
      .subscribe((allRecipeClassificationTypes) => {
        allRecipeClassificationTypes = this.refDataService.toArray(allRecipeClassificationTypes);
        this.recipeClassificationTypes = allRecipeClassificationTypes.map((classificationTypes: DataRef) => ({
          ...classificationTypes,
          translatedName: this.lang === 'fr' ? classificationTypes.name.GS_FR?.fr : classificationTypes.name.GS_INT?.en,
        }));
      });

    this.refDataService
      .getByKey('recipeClassifications', true, recipe.domain, recipe.market, recipe.lang)
      .subscribe((allRecipeClassifications) => {
        this.recipeClassifications = this.refDataService.toArray(allRecipeClassifications);
        this.recipeClassifications = this.recipeClassifications.map((classification: DataRef) => ({
          ...classification,
          translatedName: classification.labelInt,
        }));
        this.classificationRichInList = this.filterWithKey(this.recipeClassifications, 'RICH_IN');
        this.formClassif.controls.classificationRichIn.patchValue(
          recipe.classifications?.find((classif: any) =>
            this.classificationRichInList.some((classifRI: any) => classif.KEY === classifRI.KEY),
          ) ?? '',
        );
      });
  }
}
